import React, { useState, useEffect } from 'react';
import type { ExpressionNode } from '../../state/types';
import OperandDropZone from './OperandDropZone';
import LiteralEditor from './LiteralEditor';
import SeriesPill from './SeriesPill';
import { useAppStore } from '../../state/store';
import { getIndicatorDisplayName, getEmittedFields } from '../../utils/indicatorDisplayRegistry';
import { getSeriesLabelForIndicator, getRollingSeriesOptions } from './expressionEditorSelectors';
import { functionRegistry } from '../../utils/functionRegistry';

type Props = {
  index: number;
  expressionId: string;
  operandId: string | undefined;
  label: string | undefined;
  expressions: Record<string, ExpressionNode>;
  isEditing: boolean;
  literalInput: string;
  onLiteralChange: (v: string) => void;
  onLiteralSubmit: (index: number, value: string) => void; 
  onCancelEdit: () => void;
  onRemove?: () => void;
  onClick?: () => void;
  onEditSeries?: () => void;
  renderedChildExpression?: React.ReactNode;
  isExtraSlot?: boolean;
  isHovered?: boolean;
};

const OperandSlot: React.FC<Props> = ({
  index,
  expressionId,
  operandId,
  label,
  expressions,
  isEditing,
  literalInput,
  onLiteralChange,
  onLiteralSubmit,
  onCancelEdit,
  onRemove,
  onClick,
  onEditSeries,
  renderedChildExpression,
  isExtraSlot,
  isHovered,
}) => {
  const instruments = useAppStore((s) => s.instruments);
  const indicators = useAppStore((s) => s.indicators);
  const constants = useAppStore((s) => s.constants);

  const parentNode = expressions[expressionId];
  const parentFunction = parentNode?.type === 'function' ? functionRegistry.get(parentNode.functionId) : null;

  const [expectedType, setExpectedType] = useState<'number' | 'string'>('number');
  const [enumValues, setEnumValues] = useState<string[]>([]);

  useEffect(() => {
    if (isEditing && !operandId && parentNode?.type === 'function') {
      const firstOperandId = parentNode.operandIds[0];
      const firstOperand = firstOperandId ? expressions[firstOperandId] : null;

      const firstFunction = firstOperand?.type === 'function'
        ? functionRegistry.get(firstOperand.functionId)
        : null;

      // Existing logic (keep it for now)
      const type = firstFunction?.returnType || parentFunction?.returnType || 'number';
      const enums = firstFunction?.enumValues || parentFunction?.enumValues || [];

      setExpectedType(type);
      setEnumValues(enums);
    }
  }, [isEditing, operandId, parentNode, parentFunction, expressions, index]);

  let child: React.ReactNode = null;

  if (operandId) {
    const node = expressions[operandId];

    // 1) Literal
    if (node?.type === 'literal') {
      child = isEditing ? (
        <LiteralEditor
          value={literalInput}
          onChange={onLiteralChange}
          onSubmit={() => {
            onLiteralSubmit(index, literalInput); // Pass the current value
            onCancelEdit(); // Exit editing mode
          }}
          onCancel={onCancelEdit}
          expectedType={expectedType}
          enumValues={enumValues}
        />
      ) : (
        <span
          className="text-sm text-ink bg-newsprint-dark px-2 py-1 rounded-sm border border-rule cursor-pointer"
          onClick={onClick}
        >
          {node.value}
        </span>
      );
    }
    // 2) Series / Indicator – treat missing instrument/indicator as empty slot (e.g. deleted)
    else if (node?.type === 'ohlc_series' || node?.type === 'indicator_series') {
      if (node.type === 'ohlc_series') {
        const inst = instruments.find((i) => i.id === node.seriesId);
        if (inst) {
          const offsetPart = node.offset ? `(${node.offset})` : '';
          const labelText = `${inst.symbol} ${node.field}${offsetPart}`;
          child = <SeriesPill label={labelText} onClick={onEditSeries} />;
        }
      } else {
        const ind = indicators.find((i) => i.id === node.seriesId);
        if (ind) {
          const seriesLabel = getSeriesLabelForIndicator(ind, instruments, indicators);
          const options =
            ind.type === 'rolling' ? getRollingSeriesOptions(ind.instrumentId, instruments, indicators) : undefined;
          const indName = ind.name || getIndicatorDisplayName(ind, seriesLabel, options);
          const offsetPart = node.offset ? `(${node.offset})` : '';
          const emitted = getEmittedFields(ind.type);
          const emittedKeys = Object.keys(emitted);
          let labelText: string;
          if (emittedKeys.length > 1) {
            const raw = node.field ?? emittedKeys[0] ?? 'Value';
            const formatted = emittedKeys.includes(raw) ? raw : raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
            labelText = `${indName} ${formatted}${offsetPart}`;
          } else {
            labelText = `${indName}${offsetPart}`;
          }
          child = <SeriesPill label={labelText} onClick={onEditSeries} />;
        }
      }
    }
    // 3) seriesAware – treat missing instrument/indicator as empty slot
    else if (node?.type === 'seriesAware') {
      const inst = instruments.find((i) => i.id === node.seriesId);
      const ind = indicators.find((i) => i.id === node.seriesId);
      if (inst || ind) {
        const symbol = inst?.symbol ?? (ind ? ind.name : 'Unknown');
        const fields = node.requiredFields.map(f => f.toUpperCase()).join(', ');
        const param = parentFunction?.parameters[index];
        const offsetPart = param?.type !== 'seriesAwareWindow' && node.offset ? `(${node.offset})` : '';
        const windowPart = param?.type === 'seriesAwareWindow' ? ` (${param.lookbackBars} bars)` : '';
        const labelText = `${symbol} [${fields}]${offsetPart}${windowPart}`;
        child = <SeriesPill label={labelText} onClick={onEditSeries} />;
      }
    }
    // 4) Constant – resolve by constantId, distinct styling
    else if (node?.type === 'constant') {
      const constant = constants.find((c) => c.id === node.constantId);
      const displayName = constant ? constant.name : '?';
      child = (
        <span
          className="text-sm text-ink bg-newsprint-dark px-2 py-1 rounded-sm border border-rule border-l-4 border-l-ink italic"
          onClick={onClick}
        >
          {displayName}
        </span>
      );
    }
    // 5) Nested expression
    else if (node) {
      child = renderedChildExpression ?? null;
    }
  } else if (isEditing) {
    child = (
      <LiteralEditor
        value={literalInput}
        onChange={onLiteralChange}
        onSubmit={() => {
          onLiteralSubmit(index, literalInput); // Pass the current value
          onCancelEdit(); // Exit editing mode
        }}
        onCancel={onCancelEdit}
        expectedType={expectedType}
        enumValues={enumValues}
      />
    );
  }

  return (
    <OperandDropZone
      id={`${expressionId}-slot-${index}`}
      child={child}
      label={label}
      onRemove={operandId ? onRemove : undefined}
      onClick={!operandId && !isEditing ? onClick : undefined}
      functionId={parentFunction?.symbol}
      existingTypes={[]}
      isExtraSlot={isExtraSlot}
      isHovered={isHovered}
    />
  );
};

export default OperandSlot;